import { MutationFulfillSellerOrderArgs, SellerOrder } from '@shopify-multiseller/graphql/partner/types';
import { useLazyQuery, useMutation } from "@apollo/client";
import { useState, useEffect } from "react";
import { Page, useNotification } from "../../components"
import { Button } from "../../components/primitives"
import { FormInput } from "../../components/form"
import { SectionCard } from "../../components/SectionCard"

import { GET_ORDER_QUERY, FULFILL_ORDER_MUTATION } from "./queries";
import { useNavigate, useParams } from "react-router-dom";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { AdminUI } from "../../ui/AdminUI";
import EmptyPage from "../EmptyPage/EmptyPage";
import { Formik } from "formik";
import * as yup from 'yup';
import { SellerOrderItem } from './SellerOrderItem';
import CustomerSection from '../SellerOrder/CustomerDetail';
import { FulfillSellerOrderLoader } from './FulfillSellerOrderLoader';


interface IFulfillSellerOrderInitialValuesProps {
    trackingNumber: string;
    shippingCompany: string;
    trackingUrl: string;
}

export const FulfillSellerOrderValidationSchema = yup.object({
    trackingNumber: yup.string(),
    shippingCompany: yup.string(),
    trackingUrl: yup.string().url('Must be a valid URL'),
});

export const FulfillSellerOrder: React.FC = () => {


    const [sellerOrder, setSellerOrder] = useState<SellerOrder | undefined>(undefined);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);

    const requiresShipping = sellerOrder?.items.some(item => (item.weight ?? 0) > 0);

    const params = useParams();
    const navigate = useNavigate();
    const notification = useNotification();

    const [getSellerOrder] = useLazyQuery<
        { product: SellerOrder },
        { id: string }
    >(GET_ORDER_QUERY);

    useEffect(() => {
        getSellerOrder({
            variables: {
                id: params.sellerOrderId!,
            }
        }).then((result: any) => {
            setSellerOrder(result.data?.sellerOrder);
            setLoading(false);
        }).catch((error) => {
            setError(error);
            setLoading(false);
        }
        );
    }, []);

    const [fulfillSellerOrder] = useMutation<
        { fulfillSellerOrder: SellerOrder },
        MutationFulfillSellerOrderArgs
    >(FULFILL_ORDER_MUTATION);

    const handleOnSubmit = async (values: IFulfillSellerOrderInitialValuesProps) => {
        setUpdating(true);
        fulfillSellerOrder({
            variables: {
                id: params.sellerOrderId!,
                details: {
                    trackingInfo: {
                        trackingNumber: values.trackingNumber,
                        shippingCompany: values.shippingCompany || undefined,
                        trackingUrl: values.trackingUrl || undefined,
                    }
                }
            }
        }).then(() => {
            notification.success({ message: 'Order fulfilled successfully' });
            setTimeout(() => {
                window.location.href = `/orders/${params.sellerOrderId}`;
            } , 3000);

        }).catch((error) => {
            setError(error);
            setUpdating(false);
            notification.error({
                message: 'An error occurred while fulfilling the order',
            });
        }
        );
    };

    if (loading) return <AdminUI><FulfillSellerOrderLoader /></AdminUI>;

    if (error)
        return (
            <AdminUI><EmptyPage
                title="This order didn't load"
                description="The connection dropped or the server is busy. The order itself is fine."
                icon={ExclamationCircleIcon}
                action={{ label: 'Back to orders', onAction: () => navigate('/orders') }}
            />
            </AdminUI>
        );

    if (!sellerOrder)
        return (
            <AdminUI><EmptyPage
                title="Order not found"
                description="It may have been cancelled, or the link may be for a different seller account."
                icon={ExclamationCircleIcon}
                action={{ label: 'Back to orders', onAction: () => navigate('/orders') }}
            />
            </AdminUI>
        );
    return (
        <AdminUI>
            <Formik<IFulfillSellerOrderInitialValuesProps>
                initialValues={{
                    trackingNumber: '',
                    shippingCompany: '',
                    trackingUrl: '',
                }}
                enableReinitialize
                validationSchema={FulfillSellerOrderValidationSchema}
                validateOnBlur={false}
                onSubmit={handleOnSubmit}
            >
                {props => {
                    return (
                        <Page
                            title={`Order #${sellerOrder.idAlias}`}
                            actions={<Button variant='primary'
                                data-testid="fulfill-submit"
                                loading={updating}
                                onClick={() => {
                                    props.handleSubmit();
                                }} >
                                Fulfill Items
                            </Button>}
                            breadcrumbs={[{ content: 'Orders', url: `/orders/` }, { content: sellerOrder.idAlias, url: `/orders/${sellerOrder.id}` }]}
                        >
                            <div className="flex flex-wrap items-start justify-center gap-5">
                                <div className="min-w-[51%] flex-[2_2_30rem] space-y-5">
                                    <SectionCard
                                        title="Items to pack"
                                        testId="fulfill-items-section"
                                        trailing={`${sellerOrder.items.length} ${sellerOrder.items.length === 1 ? 'product' : 'products'}`}
                                    >
                                        {sellerOrder.items.map((item, index) => (
                                            <SellerOrderItem item={item} key={index} />
                                        ))}
                                    </SectionCard>
                                    {requiresShipping && (
                                        <SectionCard title="Tracking details" testId="fulfill-tracking-section">
                                            <div className="space-y-4">
                                                <div className="grid gap-4 sm:grid-cols-2">
                                                    <FormInput
                                                        type="text"
                                                        label="Tracking number"
                                                        name="trackingNumber"
                                                        data-testid="fulfill-tracking-number"
                                                        autoComplete="off"
                                                        disabled={updating}
                                                    />
                                                    <FormInput
                                                        type="text"
                                                        label="Shipping carrier"
                                                        name="shippingCompany"
                                                        data-testid="fulfill-shipping-carrier"
                                                        autoComplete="off"
                                                        disabled={updating}
                                                    />
                                                </div>
                                                <FormInput
                                                    type="text"
                                                    label="Tracking URL"
                                                    name="trackingUrl"
                                                    data-testid="fulfill-tracking-url"
                                                    autoComplete="off"
                                                    disabled={updating}
                                                    placeholder="https://"
                                                />
                                                {/* Tracking is no longer write-once: it can be
                                                    corrected from the order after fulfilling. */}
                                                <p className="text-xs text-muted-foreground">
                                                    You can correct these details later from the order.
                                                </p>
                                            </div>
                                        </SectionCard>
                                    )}
                                </div>
                                <div className="min-w-0 flex-[1_1_15rem] space-y-5">
                                    <CustomerSection order={sellerOrder} />
                                </div>
                            </div>
                        </Page>);
                }}
            </Formik>
        </AdminUI>
    );
}
